import React, { MutableRefObject, useCallback, useEffect, useRef } from "react";
/* import createHistory from "history/createBrowserHistory"; */
import { createBrowserHistory } from "history";
import NProgress from "nprogress";
import { createGlobalStyle, ThemeProvider } from "styled-components";
import { darkTheme, lightTheme } from "../styles/theme";
import { useDarkMode } from "../utils/useDarkMode";
import CookieConsentBanner from "./CookieConsentBanner";
import Footer from "./Footer";
import Header from "./Header";
import { SilentAny } from "../utils/types";

const GlobalStyle = createGlobalStyle`
  html {
    box-sizing: border-box;
    font-size: 16px; /* fallback */
    font-size: min(max(16px, 4vw), 20px);
    overflow-y: scroll;
  }
  *, *:before, *:after {
    box-sizing: inherit;
    font-family: ${(props) => props.theme.font.fontFamily};
  }
  * {
  // remove default a11y border and add more specifie one
  // it will now show for tabbing but not mouse clicks
  outline: none;
  &:focus-visible {
    outline: 2px solid ${(props) =>
      props.theme.LightOrDark === "dark"
        ? props.theme.colours.orange
        : props.theme.colours.midBlue};
  }
  }
  html, body, #___gatsby, 
  // #gatsby-focus-wrapper 
  {
    height: 100%;
  }
  #___gatsby, #gatsby-focus-wrapper  {
    min-height: 100vh;
  }
  body {
    margin: 0;
    line-height: 1.5;
    background: ${(props) => props.theme.colours.background};
    // Transition between colours when switching themes
    // transition: all 0.5s ease-in-out;
    // transition-property: background, color;
  }
  h1, h2, h3 {
    margin: 0;
    lineHeight: 100%;
  }
  h1 {
    font-size: 5rem;
    font-weight: bolder;
    line-height: initial;
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
      font-size: 4rem;
    }
  }
  h2 {
    font-size: 2.2rem;
    font-weight: bolder;
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
      font-size: 1.8rem;
    }
  }
  h3 {
    font-size: 1.6rem;
    font-weight: bolder;
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
      font-size: 1.2rem;
    }
  }
  h5 {
    font-size: 0.8rem;   /* h5 used for FootnoteText, ScrollProgressBar, etc. font-size matched to form labels */
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
      font-size: 0.7rem;
    }
  }
    ul, ol {
    // padding: 0;
    margin: 0;
    // list-style-position: inside;
  }
  legend {
    font-size: 2.2rem;
    font-weight: bolder;
    margin: 0;
    line-height: 100%;
  }
  label {
    font-size: 0.8rem;
    font-weight: bold;
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
        font-size: 0.7rem;
    }
  }
  input, textarea {
    color: ${(props) => props.theme.colours.foreground};
    background: ${(props) => props.theme.colours.background};
    padding: 0.5rem 0;
    font-size: 1rem;
    @media (max-width: ${(props) => props.theme.screenSize.s}px) {
      font-size: 0.8rem;
    }
  }
  a {
    text-decoration: none;
    color: ${(props) => props.theme.colours.midBlue};
    transition: all 0.2s ease-in;
    &:active {
      outline: none;
    }
    &:hover {
      cursor: pointer;
      color: ${(props) => props.theme.colours.orange};
    }
    // &:visited {
    //   color: ${(props) => props.theme.colours.darkBlue};
    // }
  }
`;

const Layout = ({
  children,
  location,
}: {
  location?: SilentAny /* used Silent any to fix "object is of type 'unknown'" tsc warnings */;
  // location?: LocationState;
  children?: React.ReactNode;
}): JSX.Element => {
  useEffect(() => {
    NProgress.configure({ minimum: 0.1 });
    NProgress.start();
    NProgress.done();
  }, []);

  const { theme, toggleTheme } = useDarkMode();

  const themeMode = theme === "light" ? lightTheme : darkTheme;

  const bottomOfPageRef = useRef() as MutableRefObject<HTMLDivElement>;

  const isIndexPage = location && location.pathname === "/" ? true : false;

  useEffect(() => {
    const history = createBrowserHistory();
    if (location && location.state?.referral) {
      bottomOfPageRef.current.scrollIntoView({ behavior: "smooth" });
      // This resets referral location.state.referral property,
      // so reloadig the index page after navigating from a different page
      // doesn't scroll the user to bottom of page
      const state = { ...(history.location.state as Record<string, unknown>) };
      delete state.referral;
      history.replace({ ...history.location, state });
    }
  }, []);

  const onClickScroll = useCallback(() => {
    bottomOfPageRef.current.scrollIntoView({ behavior: "smooth" });
  }, []);

  return (
    <ThemeProvider theme={themeMode}>
      {/* <AppProvider> */}
      <GlobalStyle />
      <div
        // flex styles on body make footer sticky in conjunction with "flex: 1" on main in MainStyles
        style={{ display: "flex", minHeight: "100vh", flexDirection: "column" }}
      >
        <CookieConsentBanner />
        <Header
          toggleTheme={toggleTheme}
          onClickScroll={isIndexPage ? onClickScroll : undefined}
        />

        {children}
        <Footer />
        <div ref={bottomOfPageRef} />
      </div>
      {/* </AppProvider> */}
    </ThemeProvider>
  );
};

export default Layout;
